import { initializeAnalyticsFx } from 'model/helpers/analytics.model';
import { delay } from 'patronum';
import { attach, combine, createEffect, forward, guard, merge, sample } from 'effector';

import { applyInitialTheme, updateThemeFx } from 'model/helpers/theme';
import { jwtDecode } from 'libs/jwt-decode';
import { clearStores } from 'model/root.model';
import { http, requestFx } from 'model/helpers/http';
import { mountFx, unmountFx } from 'model/mount.model';
import { closeWindowFx } from 'model/helpers/close-window';
import { cancel, completed } from 'model/helpers/subscribers.model';
import { fetchDefaultLocalization, fetchLocalization, localization$, localizationOptions$, updateLocalization } from 'model/localization.model';
import { AnalyticsEvent, sendEvent } from 'model/helpers/events.model';
import { ErrorType, errorType$, setError } from 'model/error.model';
import { disconnectFallbackSocket, fallbackCancel, onFallbackFinish } from 'model/flows/fallback.model';
import { CrossDeviceParams, MountType, SessionTokenPayload } from 'model/domain';
import { onVerificationFinish, verificationCancel } from 'model/flows/verification.model';
import {
	countryCode$,
	fetchCountryFx,
	initialize,
	ipAddress$,
	mountType$,
	onMountVerify,
	params$,
	sessionToken$,
	unmountVerify,
} from 'model/main.model';
import {
	destroyRisk,
	initDesktopRisk,
	initMobileRisk,
} from 'model/helpers/risk.model';
import {
	fetchSessionConfig,
	sessionConfig$,
	updateApplicationKey,
	updateSessionToken,
} from 'model/session.model';
import {
	connectCrossDeviceSocket,
	crossDeviceCancel,
	crossDeviceFinish,
	onCrossDeviceCanceled,
	onCrossDeviceParams,
} from 'model/flows/cross-device.model';

const onMount = sample({
	source: { params: params$, mountType: mountType$ },
	clock: onMountVerify,
	fn: ({ params, mountType }, { container, error }) => ({
		container,
		type: mountType,
		mode: params?.options?.mode,
		error: error,
	}),
	// @ts-ignore
	target: mountFx,
});
const cancelVerificationFx = attach({
	effect: http.post,
	source: {
		sessionToken: sessionToken$,
	},
	mapParams: (_: void, { sessionToken }) => {
		const payload: SessionTokenPayload = jwtDecode(sessionToken);

		return {
			url: `/sessions/${payload.session_id}/cancel`,
		};
	},
});
fetchCountryFx
	.use(async () => {
		const res = await fetch('https://ipinfo.io/json', {
			headers: {
				Authorization: 'Bearer b061a0dce56d61',
			},
		});
		return res.json();
	});

countryCode$
	.on(fetchCountryFx.doneData, (_, result) => result.country.toLowerCase())
	.on(fetchCountryFx.fail, () => 'us');
ipAddress$
	.on(fetchCountryFx.doneData, (_, result) => result.ip);

guard({
	// @ts-ignore
	source: sample({
		source: { params: params$, mountType: mountType$ },
		clock: onMount,
		fn: (source): AnalyticsEvent | null => {
			if ('options' in source.params) {
				return {
					event: 'widget_initialized',
					event_attributes: {
						implementation_type: source.params?.options.mode || 'modal',
					},
				};
			} else {
				return null;
			}
		},
	}),
	filter: event => event !== null,
	target: sendEvent,
});
forward({
	from: onMount,
	to: createEffect({
		handler: () => {
			const link = document.createElement('link');
			link.setAttribute('href', 'https://cdn.hive.id/assets/flags.min.css');
			link.setAttribute('rel', 'stylesheet');

			document.head.appendChild(link);
		},
	}),
});
guard({
	source: sample({
		source: mountType$,
		clock: onMount,
		fn: (mountType) => mountType,
	}),
	filter: (mountType: MountType) => [MountType.verification, MountType.fallback]
		.includes(mountType),
	target: initialize,
});
guard({
	// @ts-ignore
	source: sample({
		source: { params: params$, mountType: mountType$ },
		clock: onMount,
		fn: ({ params, mountType }) => ({ mountType, room: (params as CrossDeviceParams).room }),
	}),
	// @ts-ignore
	filter: ({ mountType }) => [MountType.crossDeviceVerification, MountType.invalid_device]
		.includes(mountType),
	target: connectCrossDeviceSocket.prepend(({ room }: { room: string }) => ({ room })),
});
guard({
	source: sample({
		source: mountType$,
		clock: onMount,
		fn: (mountType) => mountType,
	}),
	filter: (mountType: MountType) => mountType === MountType.invalid_device,
	target: setError.prepend(() => ErrorType.invalid_device),
});
forward({
	from: onMount,
	to: fetchCountryFx,
});

forward({
	from: onCrossDeviceParams,
	to: [
		updateLocalization.prepend(({ localizationOptions }) => (localizationOptions)),
		updateApplicationKey.prepend(({ applicationKey }) => applicationKey),
		updateSessionToken.prepend(({ token }) => token),
	],
});
forward({
	from: sample({
		source: {
			params: params$,
			sessionToken: sessionToken$,
		},
		clock: initialize,
	}),
	to: [
		updateLocalization.prepend(({ params }) => ({
			language: params?.options.language,
			languageSelector: params?.options.languageSelector,
		})),
		updateApplicationKey.prepend(({ params }) => params.applicationKey),
		updateSessionToken.prepend(({ sessionToken }) => sessionToken),
	],
});

forward({
	from: fetchSessionConfig.doneData,
	to: [
		updateLocalization.prepend(result => result.localization),
		initializeAnalyticsFx.prepend(result => ({ session: result.session_id, tenant: result.tenant })),
	],
});
guard({
	source: combine(errorType$, localization$, (error, localization) => error !== null && localization === null),
	filter: Boolean,
	target: fetchDefaultLocalization,
});
sample({
	source: localizationOptions$,
	clock: fetchSessionConfig.done,
	target: fetchLocalization,
});
sample({
	source: { mountType: mountType$, session: sessionConfig$ },
	clock: fetchSessionConfig.done,
	fn: ({ mountType, session }) => ({ mountType, session }),
	target: createEffect({
		handler: ({ mountType, session }) => {
			if ([MountType.fallback, MountType.invalid_device].includes(mountType)) {
				initDesktopRisk({ sessionId: session.id });
			} else {
				initMobileRisk({ sessionId: session.id });
			}
		},
	}),
});
sample({
	source: params$,
	clock: fetchSessionConfig.doneData,
	fn: (source, clock) => {
		if ('room' in source) {
			return {
				paramsTheme: {},
				dashTheme: clock.theme,
			};
		} else {
			return {
				paramsTheme: source.options.theme || {},
				dashTheme: clock.theme,
			};
		}
	},
	target: applyInitialTheme,
});
forward({
	from: fetchSessionConfig.doneData,
	to: updateThemeFx.prepend(result => ({ theme: result.theme })),
});

const onFinish = merge([
	onFallbackFinish,
	guard({
		source: sample({
			source: mountType$,
			clock: onVerificationFinish,
		}),
		filter: mountType => mountType !== MountType.crossDeviceVerification,
	}),
]);
forward({
	from: fallbackCancel,
	to: setError,
});
const onCancel = merge([
	fallbackCancel,
	guard({
		source: sample({
			source: { mountType: mountType$, config: sessionConfig$ },
			clock: verificationCancel,
		}),
		// @ts-ignore
		filter: ({ mountType }) => mountType === MountType.verification,
	}),
]);
guard({
	source: sample({
		source: mountType$,
		clock: onVerificationFinish,
	}),
	filter: mountType => mountType === MountType.crossDeviceVerification,
	target: crossDeviceFinish,
});
forward({
	from: delay({
		source: guard({
			source: sample({
				source: { mountType: mountType$, config: sessionConfig$, errorType: errorType$ },
				clock: verificationCancel,
			}),
			filter: ({ mountType }) => mountType === MountType.crossDeviceVerification,
			// @ts-ignore
			target: crossDeviceCancel.prepend(({ errorType }) => errorType),
		}),
		timeout: 100,
	}),
	to: closeWindowFx,
});
forward({
	from: unmountVerify,
	to: unmountFx,
});
forward({
	from: merge([crossDeviceFinish, onCrossDeviceCanceled]),
	to: closeWindowFx,
});
forward({
	from: onFinish,
	to: [
		completed,
		unmountFx,
		disconnectFallbackSocket,
	],
});
forward({
	from: onCancel,
	to: [
		disconnectFallbackSocket,
		destroyRisk,
		cancelVerificationFx,
		cancel,
		unmountFx,
	],
});
forward({
	from: delay({
		source: unmountFx.done,
		timeout: 100,
	}),
	to: [clearStores],
});

guard({
	source: sample({
		source: { mountType: mountType$ },
		clock: requestFx.fail,
		fn: (source, fail) => {
			if (fail.error.status && fail.error.status !== 500) { return null; }

			return ErrorType.unhandled;
		},
	}),
	filter: errorType => errorType !== null,
	target: setError,
});
